import { getPetTagsThunk, MarketplaceStatus } from '@spike/marketplace-action';
import { MarketplacePetTags } from '@spike/marketplace-model';
import { Option, PetTypeIds } from '@spike/model';
import { Room, RoomRule, RoomRuleTypeIds } from '@spike/room-model';
import useNonInitialEffect from '@versiondos/hooks';
import DescriptionText from 'components/UI/V3/DescriptionText';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { Switch } from 'components/UI/V3/Forms/Switch';
import { GenericDeleteModal } from 'components/UI/V3/Modals/DeleteModal';
import MultipleSelector from 'components/UI/V3/MultipleSelector';
import PetTypesSelector from 'components/UI/V3/PetTypesSelector';
import Spinner from 'components/UI/V3/Spinner';
import TagsSelector from 'components/UI/V3/TagsSelector';
import { useApiClientWrapper, useMarketplace, useMasterData } from 'hooks';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { v4 as uuid } from 'uuid';
import AddRuleButton from './AddRuleButton';
import { Button } from './Button';
import styles from './Rules.module.scss';
import TotalCountRule from './TotalCountRule';
import RuleSize, { RuleSizeData } from './RuleSize';
import Or from './Or';
import WeightRule from './WeightRule';
import WeightRangeRule from './WeightRangeRule';

interface RulesProps {
    room: Room;
    saving?: boolean;
    className?: string;
    onChange?: (room: Room) => void;
    onSave?: () => void;
}

const getSelectedLabel = (selectedIds: Array<number>, breeds: Array<Option<number>>) => {
    const selectedBreeds = breeds.filter(breed => selectedIds.includes(breed.id));
    const firstTwoElements = selectedBreeds
        .slice(0, 2)
        .map(breed => breed.name)
        .join(', ');
    return selectedBreeds.length > 2 ? `${firstTwoElements} & ${selectedBreeds.length - 2} breeds` : firstTwoElements;
};

export const Rules: FunctionComponent<RulesProps> = props => {
    const marketplace = useMarketplace();
    const masterData = useMasterData();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const dogBreeds = masterData.breeds.dogs;
    const catsBreeds = masterData.breeds.cats;

    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);
    const petTags = useSelector<RootState, MarketplacePetTags>(state => state.marketplace.petTags);

    const enabledPetTypes = masterData.petTypes.filter(
        petType =>
            (petType.id === PetTypeIds.DOGS && marketplace.petTypes.dogs.enabled) ||
            (petType.id === PetTypeIds.CATS && marketplace.petTypes.cats.enabled) ||
            (petType.id === PetTypeIds.EXOTICS && marketplace.petTypes.exotics.enabled)
    );

    const petTagOptions = useMemo(
        () =>
            [
                ...petTags.positive.sort((tag, otherTag) => tag.name.localeCompare(otherTag.name)),
                ...petTags.negative.sort((tag, otherTag) => tag.name.localeCompare(otherTag.name))
            ].map(tag => ({ id: tag.id, name: tag.name, isRemovable: true })),
        [petTags]
    );

    const [loading, setLoading] = useState({ petTags: true });
    const [weightRangeRuleError, setWeightRangeRuleError] = useState('');
    const [show, setShow] = useState({
        restrictions:
            props.room.restrictions.breeds.dogBreedIds.length +
                props.room.restrictions.breeds.catBreedIds.length +
                props.room.restrictions.petTagIds.length >
            0,
        breeds:
            props.room.restrictions.breeds.dogBreedIds.length + props.room.restrictions.breeds.catBreedIds.length > 0,
        tags: props.room.restrictions.petTagIds.length > 0
    });

    const [ruleToBeDeleted, setRuleToBeDeleted] = useState<{ uuid: string; type: string } | null>(null);
    const [ruleErrors, setRuleErrors] = useState<Record<string, boolean>>({});

    const error = Object.values(ruleErrors).some(hasError => hasError);

    const handleRuleError = useCallback((ruleUuid: string, hasError: boolean) => {
        setRuleErrors(prev => ({
            ...prev,
            [ruleUuid]: hasError
        }));
    }, []);

    const sizeRuleErrorCallbacks = useMemo(() => {
        const callbacks: Record<string, (hasError: boolean) => void> = {};
        props.room.rules
            .filter(r => !r.destroy)
            .filter(r => r.ruleTypeId === RoomRuleTypeIds.petSizeRuleId)
            .forEach(rule => {
                callbacks[rule.uuid] = (hasError: boolean) => handleRuleError(rule.uuid, hasError);
            });
        return callbacks;
    }, [props.room.rules, handleRuleError]);

    useEffect(() => {
        if (petTags.negative.length === 0 && petTags.positive.length === 0) {
            dispatch(getPetTagsThunk(apiClientWrapper));
        } else {
            setLoading(prev => ({ ...prev, petTags: false }));
        }
    }, []);

    useNonInitialEffect(() => {
        switch (marketplaceStatus) {
            case MarketplaceStatus.GetPetTagsSuccess:
            case MarketplaceStatus.Error:
                setLoading(prev => ({ ...prev, petTags: false }));
        }
    }, [marketplaceStatus]);

    const hasInvalidSizeRules = useMemo(() => {
        const sizeRules = props.room.rules
            .filter(r => !r.destroy)
            .filter(r => r.ruleTypeId === RoomRuleTypeIds.petSizeRuleId);

        if (sizeRules.length === 0) {
            return false;
        }

        return sizeRules.some(rule => {
            try {
                const sizes: Array<RuleSizeData> = JSON.parse(rule.data);
                return sizes.some(size => !size.sizeId || size.sizeId === '');
            } catch {
                return true;
            }
        });
    }, [props.room.rules]);

    const hasInvalidPetWeightRules = useMemo(() => {
        const petWeightRules = props.room.rules
            .filter(r => !r.destroy)
            .filter(r => r.ruleTypeId === RoomRuleTypeIds.totalPetWeightRuleId);

        if (petWeightRules.length === 0) {
            return false;
        }

        return petWeightRules.some(rule => {
            try {
                const weights = JSON.parse(rule.data);
                return !weights.totalWeight || weights.totalWeight === null;
            } catch {
                return false;
            }
        });
    }, [props.room.rules]);

    const hasInvalidWeightRangeRule = useCallback((rule: RoomRule): boolean => {
        try {
            const data = JSON.parse(rule.data);
            const weightFromRange = data.weightFrom;
            const weightToRange = data.weightTo;

            if (!weightFromRange && !weightToRange) {
                setWeightRangeRuleError('Minimum and maximum values are required.');
                return true;
            } else if (!weightFromRange) {
                setWeightRangeRuleError('Minimum value is required.');
                return true;
            } else if (!weightToRange) {
                setWeightRangeRuleError('Maximum value is required.');
                return true;
            }

            if (weightFromRange && weightToRange) {
                if (weightFromRange > weightToRange) {
                    setWeightRangeRuleError('Minimum value cannot be greater than maximum value.');
                    return true;
                } else if (weightFromRange === weightToRange) {
                    setWeightRangeRuleError('Minimum value cannot be equal to maximum value.');
                    return true;
                } else {
                    setWeightRangeRuleError('');
                    return false;
                }
            }
            setWeightRangeRuleError('');
            return false;
        } catch {
            setWeightRangeRuleError('');
            return false;
        }
    }, []);

    const hasInvalidWeightRangeRules = useMemo(() => {
        const weightRangeRules = props.room.rules
            .filter(r => !r.destroy)
            .filter(r => r.ruleTypeId === RoomRuleTypeIds.totalWeightRangeRuleId);

        if (weightRangeRules.length === 0) {
            return false;
        }

        return weightRangeRules.some(rule => hasInvalidWeightRangeRule(rule));
    }, [props.room.rules, hasInvalidWeightRangeRule]);

    const allSizeCombinationsUsed = useMemo(() => {
        const totalAvailableSizeCombinations =
            (props.room.petTypes.some(petType => petType.id === PetTypeIds.DOGS)
                ? marketplace.petTypes.dogs.sizes.length
                : 0) +
            (props.room.petTypes.some(petType => petType.id === PetTypeIds.CATS)
                ? (marketplace.petTypes.cats.sizes ?? []).length
                : 0) +
            (props.room.petTypes.some(petType => petType.id === PetTypeIds.EXOTICS)
                ? (marketplace.petTypes.exotics.sizes ?? []).length
                : 0);

        const usedSizeCombinations = new Set(
            props.room.rules
                .filter(r => !r.destroy)
                .filter(r => r.ruleTypeId === RoomRuleTypeIds.petSizeRuleId)
                .flatMap(rule => {
                    try {
                        return (JSON.parse(rule.data) as Array<RuleSizeData>)
                            .filter(size => !!size.petTypeId && !!size.sizeId)
                            .map(size => `${size.petTypeId}-${size.sizeId}`);
                    } catch {
                        return [];
                    }
                })
        );

        return totalAvailableSizeCombinations > 0 && usedSizeCombinations.size >= totalAvailableSizeCombinations;
    }, [props.room.rules, props.room.petTypes, marketplace.petTypes]);

    const disabledSave =
        props.room.petTypes.length === 0 ||
        error ||
        hasInvalidSizeRules ||
        hasInvalidWeightRangeRules ||
        hasInvalidPetWeightRules;

    const changeRestrictionsHandler = (on: boolean) => {
        setShow(prev => ({ ...prev, restrictions: on, breeds: false, tags: false }));
        props.onChange?.({
            ...props.room,
            restrictions: { breeds: { dogBreedIds: [], catBreedIds: [] }, petTagIds: [] }
        });
    };

    const changeShowBreedsHandler = (checked: boolean) => {
        setShow(prev => ({ ...prev, breeds: checked }));
        props.onChange?.({
            ...props.room,
            restrictions: { ...props.room.restrictions, breeds: { dogBreedIds: [], catBreedIds: [] }, petTagIds: [] }
        });
    };

    const changeShowTagsHandler = (checked: boolean) => {
        setShow(prev => ({ ...prev, tags: checked }));
        props.onChange?.({ ...props.room, restrictions: { ...props.room.restrictions, petTagIds: [] } });
    };

    const changeRestrictedDogBreedsHandler = (breedIds: Array<number>) => {
        props.onChange?.({
            ...props.room,
            restrictions: {
                ...props.room.restrictions,
                breeds: { ...props.room.restrictions.breeds, dogBreedIds: breedIds }
            }
        });
    };

    const changeRestrictedCatBreedsHandler = (breedIds: Array<number>) => {
        props.onChange?.({
            ...props.room,
            restrictions: {
                ...props.room.restrictions,
                breeds: { ...props.room.restrictions.breeds, catBreedIds: breedIds }
            }
        });
    };

    const createEmptyQuantityRule = (): RoomRule => ({
        uuid: uuid(),
        ruleTypeId: RoomRuleTypeIds.totalCountRuleId,
        data: JSON.stringify({ uuid: uuid(), quantity: 1 })
    });

    const createEmptySizeRule = (): RoomRule => ({
        uuid: uuid(),
        ruleTypeId: RoomRuleTypeIds.petSizeRuleId,
        data: JSON.stringify([{ uuid: uuid(), quantity: 1, petTypeId: null, sizeId: null }])
    });

    const createEmptyWeightRule = (): RoomRule => ({
        uuid: uuid(),
        ruleTypeId: RoomRuleTypeIds.totalPetWeightRuleId,
        data: JSON.stringify([{ uuid: uuid(), totalWeight: null }])
    });

    const createEmptyWeightRangeRule = (): RoomRule => ({
        uuid: uuid(),
        ruleTypeId: RoomRuleTypeIds.totalWeightRangeRuleId,
        data: JSON.stringify({ uuid: uuid(), quantity: 1, weightFrom: null, weightTo: null })
    });

    const changePetTypeHandler = (petTypesIds: Array<string>) => {
        const updatedRoom = {
            ...props.room,
            petTypes: petTypesIds
                .map(id => masterData.petTypes.find(petType => petType.id === id))
                .filter(petType => petType != undefined),
            restrictions: {
                breeds: {
                    dogBreedIds: petTypesIds.includes(PetTypeIds.DOGS)
                        ? [...props.room.restrictions.breeds.dogBreedIds]
                        : [],
                    catBreedIds: petTypesIds.includes(PetTypeIds.CATS)
                        ? [...props.room.restrictions.breeds.catBreedIds]
                        : []
                },
                petTagIds: petTypesIds.length === 0 ? [] : [...props.room.restrictions.petTagIds],
                rules:
                    petTypesIds.length === 0
                        ? props.room.rules.map(rule => ({ ...rule, destroy: true }))
                        : props.room.rules.length > 0
                        ? props.room.rules.map(rule => ({ ...rule, destroy: false }))
                        : [createEmptyQuantityRule()]
            }
        };
        props.onChange?.(updatedRoom);

        if (petTypesIds.length === 0) {
            setShow(prev => ({ ...prev, restrictions: false, breeds: false, tags: false }));
        } else if (!petTypesIds.some(id => [PetTypeIds.DOGS.toString(), PetTypeIds.CATS.toString()].includes(id))) {
            setShow(prev => ({ ...prev, breeds: false }));
        }
    };

    const selectTagHandler = (id: string) => {
        props.onChange?.({
            ...props.room,
            restrictions: {
                ...props.room.restrictions,
                petTagIds: [...props.room.restrictions.petTagIds.filter(tagId => tagId !== id), id]
            }
        });
    };

    const selectAllTagsHandler = (ids: Array<string>) => {
        props.onChange?.({
            ...props.room,
            restrictions: {
                ...props.room.restrictions,
                petTagIds: [...props.room.restrictions.petTagIds.filter(tagId => !ids.includes(tagId)), ...ids]
            }
        });
    };

    const removeTagHandler = (id: string) => {
        props.onChange?.({
            ...props.room,
            restrictions: {
                ...props.room.restrictions,
                petTagIds: props.room.restrictions.petTagIds.filter(tagId => tagId !== id)
            }
        });
    };

    const changeRule = (rule: RoomRule) => {
        props.onChange?.({
            ...props.room,
            rules: props.room.rules.map(r => (r.uuid === rule.uuid ? { ...rule } : r))
        });
    };

    const addRuleHandler = (type: 'count' | 'size' | 'weight' | 'weightRange') => {
        if (type === 'count') {
            props.onChange?.({
                ...props.room,
                rules: [...props.room.rules, createEmptyQuantityRule()]
            });
        } else if (type === 'size') {
            props.onChange?.({
                ...props.room,
                rules: [...props.room.rules, createEmptySizeRule()]
            });
        } else if (type === 'weight') {
            props.onChange?.({
                ...props.room,
                rules: [...props.room.rules, createEmptyWeightRule()]
            });
        } else if (type === 'weightRange') {
            props.onChange?.({
                ...props.room,
                rules: [...props.room.rules, createEmptyWeightRangeRule()]
            });
        }
    };

    const deleteRule = (ruleUuid: string) => {
        props.onChange?.({
            ...props.room,
            rules: props.room.rules
                .map(r => (r.uuid === ruleUuid ? { ...r, destroy: true } : r))
                .filter(r => r.id || !r.destroy)
        });

        setRuleErrors(prev => {
            const updated = { ...prev };
            delete updated[ruleUuid];
            return updated;
        });
    };

    const deleteRuleHandler = (ruleUuid: string, type: number) => {
        switch (type) {
            case RoomRuleTypeIds.totalCountRuleId:
                setRuleToBeDeleted({ uuid: ruleUuid, type: 'By Total Count' });
                break;
            case RoomRuleTypeIds.petSizeRuleId:
                setRuleToBeDeleted({ uuid: ruleUuid, type: 'By Pet Size' });
                break;
            case RoomRuleTypeIds.totalPetWeightRuleId:
                setRuleToBeDeleted({ uuid: ruleUuid, type: 'By Total Pet Weight' });
                break;
            case RoomRuleTypeIds.totalWeightRangeRuleId:
                setRuleToBeDeleted({ uuid: ruleUuid, type: 'By Weight Range' });
                break;
            default:
                setRuleToBeDeleted({ uuid: ruleUuid, type: '' });
        }
    };

    return (
        <>
            <div className={styles.container}>
                <PetTypesSelector
                    petTypes={enabledPetTypes}
                    selectedPetTypeIds={props.room.petTypes.map(petType => petType.id)}
                    onChange={changePetTypeHandler}
                />
                {props.room.petTypes.length > 0 && (
                    <>
                        <Switch
                            inlineLabel="Restricted Pets"
                            checked={show.restrictions}
                            onChange={changeRestrictionsHandler}
                        />
                        <DescriptionText text="Add restrictions to block certain pets from booking this space online and get alerts when booking internally." />
                        {show.restrictions && (
                            <>
                                {props.room.petTypes.some(petType =>
                                    [PetTypeIds.DOGS.toString(), PetTypeIds.CATS.toString()].includes(petType.id)
                                ) && (
                                    <>
                                        <Checkbox
                                            label="Breeds Not Allowed"
                                            checked={show.breeds}
                                            onChange={changeShowBreedsHandler}
                                        />
                                        {show.breeds && (
                                            <div className={styles.breedsContainer}>
                                                {props.room.petTypes.some(
                                                    petType => petType.id === PetTypeIds.DOGS
                                                ) && (
                                                    <div className={styles.breedContainer}>
                                                        <Label>Dogs Breeds</Label>
                                                        <MultipleSelector
                                                            options={dogBreeds}
                                                            selectedIds={props.room.restrictions.breeds.dogBreedIds}
                                                            placeholder="Select Breeds"
                                                            allOptionLabel="All Breeds"
                                                            enableSearch={true}
                                                            width={348}
                                                            selectedLabel={getSelectedLabel(
                                                                props.room.restrictions.breeds.dogBreedIds,
                                                                dogBreeds
                                                            )}
                                                            onChange={changeRestrictedDogBreedsHandler}
                                                            className={styles.breedsSelector}
                                                        />
                                                    </div>
                                                )}
                                                {props.room.petTypes.some(
                                                    petType => petType.id === PetTypeIds.CATS
                                                ) && (
                                                    <div className={styles.breedContainer}>
                                                        <Label>Cats Breeds</Label>
                                                        <MultipleSelector
                                                            options={catsBreeds}
                                                            selectedIds={props.room.restrictions.breeds.catBreedIds}
                                                            placeholder="Select Breeds"
                                                            allOptionLabel="All Breeds"
                                                            enableSearch={true}
                                                            width={348}
                                                            selectedLabel={getSelectedLabel(
                                                                props.room.restrictions.breeds.catBreedIds,
                                                                catsBreeds
                                                            )}
                                                            onChange={changeRestrictedCatBreedsHandler}
                                                            className={styles.breedsSelector}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                                <Checkbox
                                    label="Pet Tags"
                                    checked={show.tags}
                                    onChange={changeShowTagsHandler}
                                />
                                {show.tags && (
                                    <>
                                        {loading.petTags ? (
                                            <Spinner />
                                        ) : (
                                            <>
                                                <DescriptionText text="Select the pet tags not allowed" />
                                                <TagsSelector
                                                    tags={petTagOptions}
                                                    selected={petTagOptions.filter(tag =>
                                                        props.room.restrictions.petTagIds.includes(tag.id)
                                                    )}
                                                    searchPlaceholder="Search tag"
                                                    direction="up"
                                                    onSelect={selectTagHandler}
                                                    onSelectAll={selectAllTagsHandler}
                                                    onRemove={removeTagHandler}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        <Label>Capacity Rules</Label>
                        <DescriptionText text="The maximum number of pets allowed in a room or space at one time." />
                        <div className={styles.rulesContainer}>
                            {props.room.rules.filter(r => !r.destroy).length === 0 && (
                                <AddRuleButton onAdd={addRuleHandler} />
                            )}

                            {props.room.rules
                                .filter(r => !r.destroy)
                                .filter(r => r.ruleTypeId === RoomRuleTypeIds.totalCountRuleId)
                                .map(rule => (
                                    <TotalCountRule
                                        key={rule.id}
                                        rule={rule}
                                        onChange={changeRule}
                                        onDelete={() => deleteRuleHandler(rule.uuid, rule.ruleTypeId)}
                                    />
                                ))}

                            {props.room.rules
                                .filter(r => !r.destroy)
                                .filter(r => r.ruleTypeId === RoomRuleTypeIds.totalPetWeightRuleId)
                                .map(rule => (
                                    <WeightRule
                                        key={rule.id}
                                        rule={rule}
                                        onChange={changeRule}
                                        onDelete={() => deleteRuleHandler(rule.uuid, rule.ruleTypeId)}
                                        error={hasInvalidPetWeightRules ? 'Maximum weight is required.' : ''}
                                    />
                                ))}
                            {props.room.rules.some(r => r.ruleTypeId === RoomRuleTypeIds.petSizeRuleId) && (
                                <div className={styles.rulesOrContainer}>
                                    {props.room.rules
                                        .filter(r => !r.destroy)
                                        .filter(r => r.ruleTypeId === RoomRuleTypeIds.petSizeRuleId)
                                        .map((rule, index) => {
                                            const usedRulesInOtherSizeRules = props.room.rules
                                                .filter(r => !r.destroy)
                                                .filter(r => r.ruleTypeId === RoomRuleTypeIds.petSizeRuleId)
                                                .filter(r => r.uuid !== rule.uuid)
                                                .flatMap(r => {
                                                    try {
                                                        const parsed = JSON.parse(r.data) as Array<RuleSizeData>;
                                                        return parsed.map(size => ({
                                                            petTypeId: size.petTypeId,
                                                            sizeId: size.sizeId
                                                        }));
                                                    } catch {
                                                        return [];
                                                    }
                                                });

                                            return (
                                                <>
                                                    <RuleSize
                                                        key={rule.id}
                                                        rule={rule}
                                                        roomPetTypeIds={props.room.petTypes.map(petType => petType.id)}
                                                        usedRulesInOtherSizeRules={usedRulesInOtherSizeRules}
                                                        onChange={changeRule}
                                                        onDelete={() => deleteRuleHandler(rule.uuid, rule.ruleTypeId)}
                                                        onError={sizeRuleErrorCallbacks[rule.uuid]}
                                                    />
                                                    {index <
                                                        props.room.rules
                                                            .filter(r => !r.destroy)
                                                            .filter(r => r.ruleTypeId === RoomRuleTypeIds.petSizeRuleId)
                                                            .length -
                                                            1 && <Or />}
                                                </>
                                            );
                                        })}
                                    <span
                                        className={`${styles.addOrLink} ${
                                            allSizeCombinationsUsed ? styles.addOrLinkDisabled : ''
                                        }`}
                                        onClick={allSizeCombinationsUsed ? undefined : () => addRuleHandler('size')}
                                    >
                                        + Add &rdquo;Or&rdquo;
                                    </span>
                                </div>
                            )}
                            {props.room.rules
                                .filter(r => !r.destroy)
                                .filter(r => r.ruleTypeId === RoomRuleTypeIds.totalWeightRangeRuleId)
                                .map(rule => (
                                    <WeightRangeRule
                                        key={rule.id}
                                        rule={rule}
                                        onChange={changeRule}
                                        onDelete={() => deleteRuleHandler(rule.uuid, rule.ruleTypeId)}
                                        error={weightRangeRuleError}
                                    />
                                ))}
                        </div>
                    </>
                )}
                <Button
                    label="Save"
                    disabled={disabledSave}
                    saving={props.saving}
                    onClick={props.onSave}
                />
            </div>
            {ruleToBeDeleted && (
                <GenericDeleteModal
                    title={<span className={styles.modalTitle}>Delete Rule</span>}
                    body={
                        <>
                            <p className={styles.modalText}>Are you sure you want to delete the </p>
                            <p className={styles.modalText}>
                                <span className={styles.modalTextStrong}>Rule – {ruleToBeDeleted.type}</span>?
                            </p>
                        </>
                    }
                    onConfirm={() => {
                        deleteRule(ruleToBeDeleted.uuid);
                        setRuleToBeDeleted(null);
                    }}
                    onCancel={() => setRuleToBeDeleted(null)}
                />
            )}
        </>
    );
};

export default Rules;
